import React from 'react'
import Auth2 from 'plugin/src/shared/Auth2'
import { getApplicationName, getApplicationView } from '@mote/helpers'
import { canAccessLocalStorage } from '@mote/helpers/assertion'
import {
  SAMPLEABLE_EVENT_WHITELIST,
  SAMPLE_CHARS,
  WEB_STORAGE_PREFIX_KEYS
} from '@mote/helpers/constants'
import {
  getContainingUrlFromActiveTab,
  getExtensionVersion,
  isInExtension
} from '@mote/helpers/extension'
import { getOrMakeAnonymousUserId } from '@mote/helpers/identity'
import window from '@mote/helpers/injectChromeObject'
import * as Sentry from '@sentry/browser'
import { apiClient } from '../../client'
import { useGenericRequestHandler } from '../../handlers'

const inExtension = isInExtension()

export const useTrackMutation = (route = null) => {
  const mounted = React.useRef(false)
  const genericRequestHandler = useGenericRequestHandler({
    route: route ?? 's/track/everything',
    method: 'POST',
    responseType: 'text',
    options: {
      json: inExtension ? extensionDefaultPayload() : webDefaultPayload()
    }
  })

  const original = genericRequestHandler.mutate

  async function handleTrack(payload) {
    const hasLocalStorageAccess = await canAccessLocalStorage()
    if (!hasLocalStorageAccess || !mounted.current) {
      return
    }
    if (inExtension) {
      return extensionTracking(payload, original)
    }
    return webTracking(payload, original)
  }

  React.useEffect(() => {
    mounted.current = true

    return () => {
      mounted.current = false
    }
  }, [])

  genericRequestHandler.mutate = handleTrack
  return genericRequestHandler
}

async function webTracking(payload, mutate) {
  return mutate({
    ...payload,
    variables: {
      // the order here is important -> overrides > defaults
      userId: await getUserId(),
      ...payload.variables
    }
  })
}

async function extensionTracking(payload, mutate) {
  if (!shouldAllowEvent(payload.variables.event)) {
    return
  }

  return mutate({
    ...payload,
    variables: {
      // the order here is important -> overrides > defaults
      ...(await contextAndDomainAndPathPrefix()),
      userId: await getUserId(),
      ...payload.variables
    }
  })
}

export function extensionDefaultPayload() {
  return {
    application: getApplicationName(),
    extensionVersion: getExtensionVersion(),
    applicationView: getApplicationView()
  }
}

export function webDefaultPayload() {
  return {
    pageContext: window.location.href,
    direct: true,
    trackingRoute: 'direct',
    trackingSource: 'website'
  }
}

// HELPERS

export async function contextAndDomainAndPathPrefix() {
  if (window.location?.protocol.includes('chrome-extension')) {
    const url = await getContainingUrlFromActiveTab(false)
    let parsedUrl = null

    try {
      parsedUrl = new URL(url)
    } catch (error) {
      /* */
    }

    return {
      pageContext: url,
      applicationDomain: parsedUrl?.hostname,
      applicationDomainPathPrefix: `${parsedUrl?.hostname}/${
        parsedUrl?.pathname.replace('/', '')?.split('/')?.[0]
      }`
    }
  } else {
    return {
      pageContext: window.location?.href,
      applicationDomain: window.location?.hostname,
      applicationDomainPathPrefix: `${window.location?.hostname}/${
        window.location?.pathname.replace('/', '')?.split('/')?.[0]
      }`
    }
  }
}

export async function getUserId() {
  if (inExtension) {
    return Auth2.getUserId()
  }

  return (
    window.localStorage?.getItem(WEB_STORAGE_PREFIX_KEYS + 'userId') ||
    getOrMakeAnonymousUserId().anonymousUserId
  )
}

export function shouldAllowEvent(event) {
  if (!event) return false
  if (!SAMPLEABLE_EVENT_WHITELIST.includes(event)) return true

  if (!Auth2.isLoggedIn()) {
    return false
  }

  const firstCharacter = Auth2.getUserId()[0]

  return SAMPLE_CHARS.includes(firstCharacter)
}

// Standalone func
export async function trackRaw(options = {}) {
  const hasLocalStorageAccess = await canAccessLocalStorage()
  if (!hasLocalStorageAccess || !shouldAllowEvent(options.variables?.event)) {
    return
  }

  const json = inExtension
    ? await extensionExtendedPayload(options.variables ?? {})
    : await webExtendedPayload(options.variables ?? {})

  if (!json.userId) {
    Sentry.captureException(
      'Trying to send s/track/everything without `userId`',
      { extra: { json }, level: 'error' }
    )
  }

  return apiClient
    .post(options.routeOverride ?? 's/track/everything', {
      json: inExtension
        ? await extensionExtendedPayload(options.variables ?? {})
        : await webExtendedPayload(options.variables ?? {})
    })
    .then(response => response.json())
    .catch(console.error)
}

export async function extensionExtendedPayload(payload = {}) {
  return {
    ...extensionDefaultPayload(),
    ...(await contextAndDomainAndPathPrefix()),
    userId: await getUserId(),
    ...payload
  }
}

export async function webExtendedPayload(payload = {}) {
  return {
    ...webDefaultPayload(),
    userId: await getUserId(),
    ...payload
  }
}
